<template>
    <ModalDialog
        :z-index="1025"
        style="z-index: 1026;"
        ref="modalDialog"
        @close="reset"
    >
        <template v-slot:title>
            {{ isEdit ? "Edit Non-Plan Discount" : "New Non-Plan Discount" }}
        </template>
        <template v-slot:body>
            <div class="form-group form-row">
                <label for="item" class="col-md-4 col-form-label"
                    >Category:</label
                >
                <div class="col-md-8">
                    <treeselect
                        ref="category-select"
                        placeholder="Category (required)"
                        :value="form.category"
                        @input="updateSelectedCategory"
                        :value-consists-of="'LEAF_PRIORITY'"
                        valueFormat="object"
                        :default-expand-level="1"
                        :disable-branch-nodes="true"
                        :searchable="true"
                        :clearable="false"
                        :class="{
                            'treeselect-invalid': formError.includes('category')
                        }"
                        :options="typesArray"
                        required
                        name="category"
                        :disabled="isLoading"
                    >
                        <label
                            style="margin-bottom: 2px;"
                            slot="option-label"
                            slot-scope="{ node }"
                            class="text-capitalize"
                        >
                            <span
                                :style="`font-weight:${
                                    node.isBranch ? 'bold' : 'normal'
                                }`"
                            >
                                {{ node.label }}
                            </span>
                        </label>
                    </treeselect>
                </div>
            </div>
            <div class="form-group form-row">
                <label for="discount" class="col-md-4 col-form-label"
                    >Amount (%):</label
                >
                <div class="col-8 col-md-4">
                    <input
                        id="discount"
                        name="discount"
                        v-model="form.discount"
                        placeholder="Discount (required)"
                        v-validate="
                            'required|min_value:0.01|max_value:99999999|numericalWithDecimal|decimal:3'
                        "
                        class="form-control"
                        :class="{
                            'border-danger': errors.has('discount')
                        }"
                    />
                </div>
            </div>
        </template>
        <template v-slot:confirmBtn>
            <div class="ml-auto">
                <div v-if="!isSaving">
                    <b-button-group>
                        <b-dropdown
                            left
                            split
                            :text="isAddNew ? 'Save + Add New' : 'Save + Done'"
                            variant="primary"
                            :disabled="isLoading"
                            @click="save(isAddNew)"
                        >
                            <b-dropdown-item @click="save(!isAddNew)">
                                {{
                                    isAddNew ? "Save + Done" : "Save + Add New"
                                }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </div>
                <loader v-else :margin="1"></loader>
            </div>
        </template>
    </ModalDialog>
</template>

<script>
import ModalDialog from "@/components/commons/modal-dialog";
import TemplateService from "@/services/templates";
import Loader from "@/module/commons/loader";
import Treeselect from "@riophae/vue-treeselect";
import ItemServices from "@/services/items";
import { forEach, sortBy, map } from "lodash";

const initialForm = () => ({
    id: null,
    category: null,
    discount: 0
});

export default {
    components: {
        ModalDialog,
        Loader,
        Treeselect
    },
    data() {
        return {
            isAddNew: false,
            form: initialForm(),
            isSaving: false,
            typesArray: [],
            formError: [],
            loading: {
                category: false,
                nonPlan: false
            },
            isEdit: false
        };
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.templateId;
        },
        details() {
            return this.$store.state.settingsTemplate.details;
        },
        isLoading() {
            return this.loading.category || this.loading.nonPlan;
        }
    },
    mounted() {},
    methods: {
        async open(item) {
            this.$refs.modalDialog.open();
            this.isEdit = item ? true : false;

            await this.loadCategory(item ? item.category_id : null);

            if (item) {
                this.form.id = item.id;
                this.form.category = {
                    id: item.item_type_category.id,
                    label: item.item_type_category.name,
                    typeId: item.item_type_category.item_type_id,
                    classId: item.item_type_category.item_class_id
                };
                this.form.discount = item.discount;
            }
        },
        reset(loadCategory = false) {
            this.isEdit = false;
            this.form = Object.assign({}, initialForm());
            this.$validator.reset();

            if (loadCategory) {
                this.loadCategory();
            }
        },
        close() {
            this.reset();
            this.$refs.modalDialog.close();
            this.$emit("close");
        },
        async getCategories(excludedItems = []) {
            this.loading.category = true;

            try {
                let self = this;
                self.typesArray = [];

                const res = await ItemServices.getCategories(0);

                forEach(res, function (type) {
                    let categories = [];
                    if (type.category.length > 0) {
                        type.category = sortBy(type.category, "name");
                        forEach(type.category, function (category) {
                            if (excludedItems.includes(category.id)) {
                                return;
                            }

                            let categoryObj = {
                                id: category.id,
                                label: category.name,
                                typeId: category.item_type_id,
                                classId: type.item_class_id
                            };

                            categories.push(categoryObj);
                        });

                        if (categories.length > 0) {
                            self.typesArray.push({
                                id: "type" + type.id,
                                label: type.value,
                                children: categories
                            });
                        }
                    }
                });

                if (
                    self.typesArray &&
                    self.typesArray.length > 0 &&
                    self.typesArray[0]?.children?.length > 0
                ) {
                    const firstCategory = self.typesArray[0].children[0];
                    self.form.category = firstCategory;
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
                this._message("Error fetching categories", "danger");
            } finally {
                this.loading.category = false;
            }
        },
        async getExistingCategories() {
            let categories = [];

            try {
                this.loading.nonPlan = true;

                const res = await TemplateService.getNonPlanDiscounts(
                    this.templateId
                );

                if (res.code === 200 && res.resources) {
                    categories = map(
                        res.resources?.non_plans,
                        element => element.category_id
                    );

                    // exclude the category that is being edited
                    if (
                        this.form.category?.id &&
                        categories.includes(this.form.category.id)
                    ) {
                        categories = categories.filter(
                            category => category !== this.form.category.id
                        );
                    }
                } else {
                    throw new Error("Error: Unable to get non-plan discounts");
                }
            } catch (error) {
                console.log(error);
                this._message(
                    "Error: Unable to get non-plan discounts",
                    "danger"
                );
            } finally {
                this.loading.nonPlan = false;
            }

            return categories;
        },
        async loadCategory(selectedCategoryId = null) {
            let excludedCategories = await this.getExistingCategories();

            if (selectedCategoryId) {
                excludedCategories = excludedCategories.filter(
                    category => category !== selectedCategoryId
                );
            }

            await this.getCategories(excludedCategories);
        },
        updateSelectedCategory(value) {
            if (value) {
                this.clearError("category");
            }

            this.form.category = value;
        },
        clearError(key) {
            this.formError = this.formError.filter(err => err !== key);
        },
        async isFormValid() {
            let isValid = await this.$validator.validateAll();

            if (!this.form.category) {
                this.formError.push("category");
                isValid = false;
            }

            return isValid;
        },
        async save(isAddNew = false) {
            const isValid = await this.isFormValid();

            if (!isValid) {
                return;
            }

            try {
                this.isSaving = true;

                const payload = {
                    id: this.form.id,
                    category_id: this.form.category.id,
                    discount: this.form.discount
                };
                let res = this.form.id
                    ? await TemplateService.updateWellnessPlanNonDiscount(
                          this.templateId,
                          payload
                      )
                    : await TemplateService.addWellnessPlanNonDiscount(
                          this.templateId,
                          payload
                      );

                if (res) {
                    this._message(
                        this.form.id
                            ? "Edits successfully saved"
                            : "Item successfully added"
                    );
                    this.$emit("onsuccess");
                    this.isAddNew = isAddNew;

                    if (isAddNew) {
                        this.reset(true);
                    } else {
                        this.close();
                    }
                }
            } catch (error) {
                console.error(error);
                this._message("Error saving data", "danger");
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>
